import React, { useState, useEffect, useRef } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@root/firebase'; // Make sure this points to your Firebase setup

// USA map data (TopoJSON)
const USA_TOPO_JSON = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const USAMap = ({
  width = 500,
  height = 300,
  scale = 600,
  defaultFill = '#DDD',
  hoverFill = '#F53',
  pressedFill = '#E42',
  borderColor = '#FFF',
  showTooltip = true,
}) => {
  const [tooltip, setTooltip] = useState({
    name: '',
    count: 0,
    x: 0,
    y: 0,
    visible: false,
  });
  const [userCounts, setUserCounts] = useState({}); // Stores user count by state
  const mapRef = useRef(null);

  // 🔥 Fetch user addresses from Firestore & count users per state
  useEffect(() => {
    const fetchUserCounts = async () => {
      try {
        const usersCollection = collection(db, 'users'); // Reference to users collection
        const usersSnapshot = await getDocs(usersCollection);

        let stateCounts = {};

        for (const userDoc of usersSnapshot.docs) {
          const addressesCollection = collection(
            db,
            `users/${userDoc.id}/address`
          );
          const addressesSnapshot = await getDocs(addressesCollection);

          addressesSnapshot.docs.forEach((addressDoc) => {
            const { region } = addressDoc.data(); // Get the state/region
            if (region) {
              stateCounts[region] = (stateCounts[region] || 0) + 1;
            }
          });
        }

        setUserCounts(stateCounts);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserCounts();
  }, []);

  // 🖱️ Handle Mouse Hover - Show State Name & User Count
  const handleMouseMove = (e, geo) => {
    if (mapRef.current) {
      const { left, top } = mapRef.current.getBoundingClientRect(); // Get container position

      const stateName = geo.properties.name; // Get state name
      const count = userCounts[stateName] || 0; // Get user count for this state

      setTooltip({
        name: stateName,
        count: count,
        x: e.clientX - left + 10, // Adjust X position relative to the map
        y: e.clientY - top - 40, // Adjust Y position
        visible: true,
      });
    }
  };

  return (
    <div
      ref={mapRef}
      style={{
        position: 'relative',
        width: `${width}px`,
        height: `${height}px`,
      }}
    >
      {/* Tooltip */}
      {showTooltip && tooltip.visible && (
        <div
          style={{
            position: 'absolute',
            top: `${tooltip.y}px`,
            left: `${tooltip.x}px`,
            background: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: '5px',
            fontSize: '14px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            transform: 'translate(-50%, -100%)',
            zIndex: 10,
          }}
        >
          <strong>{tooltip.name}</strong>
          <br />
          Users: {tooltip.count}
        </div>
      )}

      {/* USA Map */}
      <ComposableMap
        projection='geoAlbersUsa'
        projectionConfig={{ scale }}
        width={width}
        height={height}
      >
        <Geographies geography={USA_TOPO_JSON}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onMouseMove={(e) => handleMouseMove(e, geo)}
                onMouseLeave={() =>
                  setTooltip({ name: '', count: 0, x: 0, y: 0, visible: false })
                }
                style={{
                  default: { fill: defaultFill, stroke: borderColor },
                  hover: {
                    fill: hoverFill,
                    stroke: borderColor,
                    cursor: 'pointer',
                  },
                  pressed: { fill: pressedFill, stroke: borderColor },
                }}
              />
            ))
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
};

export default USAMap;
